import React from 'react';
import { useLanguage } from '@/App';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const KVKKPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  const content = {
    tr: {
      back: 'Ana Sayfaya Dön',
      title: 'KVKK Aydınlatma ve Açık Rıza Metni',
      intro: 'Bu metin, PERSONA ORGANİZASYON TİCARET LİMİTED ŞİRKETİ ("Persona Ajans" veya "Veri Sorumlusu") olarak, T.C. Kişisel Verilerin Korunması Kanunu (KVKK) uyarınca kişisel verilerinizin hangi amaçlarla işleneceği, kimlere ve hangi amaçla aktarılacağı konularında sizleri bilgilendirmek amacıyla hazırlanmıştır.',
      sectionA: 'A. İşlenen Kişisel Verileriniz:',
      sectionAIntro: 'Bu form aracılığıyla aşağıdaki kişisel verileriniz işlenecektir:',
      sectionAItems: [
        'Kimlik ve İletişim Bilgileri: Ad-Soyad, Yaşadığınız İl, WhatsApp İletişim Numarası.',
        'İçerik ve Platform Bilgileri: Instagram/TikTok kullanıcı adları, Sayfa İçerik Kategorileri, İş Birliği Yapılan Markalar, Bütçe Beklentisi (Ücret).',
        'Özel Bilgiler: Var ise Çocuğunuzun/Çocuklarınızın Yaşı/Yaşları'
      ],
      sectionB: 'B. Kişisel Veri İşleme Amacı',
      sectionBIntro: 'Toplanan kişisel verileriniz, hukuka ve dürüstlük kuralına uygun olarak aşağıdaki amaçlarla işlenecektir:',
      sectionBItems: [
        'Aday Değerlendirme: Kurumsal müşterilerimize ("Markalar") kampanya hedeflerine uygun sosyal medya etkileyicisi ("Influencer") profili seçimi ve değerlendirilmesi.',
        'İletişim: İş birliği projeleri ve potansiyel fırsatlar için sizinle Influencer olarak iletişim kurulması.',
        'Hizmet Sunumu: Persona Ajans\'ın Markalar\'a yönelik sosyal medya pazarlama ve benzer projelerinin yürütülmesi.'
      ],
      sectionC: 'C. Kişisel Verilerin Saklanma Süresi ve Aktarımı',
      sectionCItems: [
        'Verileriniz en fazla 5 (beş) yıl süreyle saklanacaktır.',
        'Verileriniz, ajansımızın hizmet verdiği veya hizmet vermek istediği Markalar ile, sadece olası sosyal medya pazarlama projelerinde değerlendirme amaçlı, KVKK\'nın öngördüğü aktarım şartlarına uygun olarak paylaşılabilecektir.'
      ]
    },
    en: {
      back: 'Back to Home',
      title: 'KVKK Information and Consent Text',
      intro: 'This text has been prepared to inform you about how your personal data will be processed and to whom and for what purpose it will be transferred in accordance with the Turkish Personal Data Protection Law (KVKK) by PERSONA ORGANIZATION TRADE LIMITED COMPANY ("Persona Agency" or "Data Controller").',
      sectionA: 'A. Your Personal Data Being Processed:',
      sectionAIntro: 'The following personal data will be processed through this form:',
      sectionAItems: [
        'Identity and Contact Information: Full Name, City of Residence, WhatsApp Contact Number.',
        'Content and Platform Information: Instagram/TikTok usernames, Page Content Categories, Collaborated Brands, Budget Expectation (Fee).',
        'Special Information: If applicable, Age(s) of Your Child/Children'
      ],
      sectionB: 'B. Purpose of Personal Data Processing',
      sectionBIntro: 'Your personal data will be processed in accordance with the law and the principle of honesty for the following purposes:',
      sectionBItems: [
        'Candidate Evaluation: Selection and evaluation of social media influencer profiles suitable for campaign objectives for our corporate clients ("Brands").',
        'Communication: Contact with you as an Influencer for collaboration projects and potential opportunities.',
        'Service Provision: Execution of Persona Agency\'s social media marketing and similar projects for Brands.'
      ],
      sectionC: 'C. Storage Period and Transfer of Personal Data',
      sectionCItems: [
        'Your data will be stored for a maximum of 5 (five) years.',
        'Your data may be shared with Brands that our agency serves or wants to serve, only for evaluation purposes in potential social media marketing projects, in accordance with the transfer conditions stipulated by KVKK.'
      ]
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-black text-white relative">
      {/* Background decoration */}
      <div className="fixed inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10">
        {/* Header */}
        <div className="bg-gray-900 border-b border-[#FFDD59]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-[#FFDD59] hover:text-yellow-400 transition font-semibold"
            >
              <ArrowLeft size={20} />
              {t.back}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl md:text-5xl font-bold text-[#FFDD59] mb-8">{t.title}</h1>

          <div className="space-y-8 text-gray-300">
            {/* Introduction */}
            <p className="text-lg leading-relaxed">{t.intro}</p>

            {/* Section A */}
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-[#FFDD59] mb-4">{t.sectionA}</h2>
              <p className="mb-4">{t.sectionAIntro}</p>
              <ul className="space-y-3">
                {t.sectionAItems.map((item, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-[#FFDD59] mt-1">▫️</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Section B */}
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-[#FFDD59] mb-4">{t.sectionB}</h2>
              <p className="mb-4">{t.sectionBIntro}</p>
              <ul className="space-y-3">
                {t.sectionBItems.map((item, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-[#FFDD59] mt-1">▫️</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Section C */}
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-[#FFDD59] mb-4">{t.sectionC}</h2>
              <ul className="space-y-3">
                {t.sectionCItems.map((item, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-[#FFDD59] mt-1">▫️</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Back button */}
          <div className="mt-12 text-center">
            <button
              onClick={() => navigate('/')}
              className="px-8 py-4 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-400 transition transform hover:scale-105"
            >
              {t.back}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KVKKPage;
